"use client";

import React, { forwardRef, useImperativeHandle, useState } from "react";
import { EmailInputStep } from "./EmailInputStep";
import { CodeVerifyStep } from "./CodeVerifyStep";
import type { EmailLoginStep, EmailLoginVariant } from "./types";
import {
  useEmailLoginLogic,
  type EmailLoginTrackingEvent,
} from "./useEmailLoginLogic";
import { SignInPageLayout } from "./layouts/SignInPageLayout";
import { VerifyEmailPageLayout } from "./layouts/VerifyEmailPageLayout";
import { DesktopLoginLayout, MobileLoginLayout } from "./layouts/LoginLayouts";
import { agreement } from "@/src/const";
import type { Web3LoginSupportedType } from "@/src/features/auth/compat/sdk-types";
import { useOnboardingHarnessQuery } from "@/src/hooks/onboarding/useOnboardingHarnessQuery";
import { useParams } from "next/navigation";
import { DeviceType } from "utils/src/uaDevicesParse/ua-device-parser";

/** 供 Modal 通过 ref 控制：在 step=code 时关闭按钮先回退到邮箱页 */
export interface EmailLoginFlowRef {
  getStep: () => EmailLoginStep;
  goBackToEmail: () => void;
}

export interface EmailLoginFlowProps {
  /** UI 变体：desktop=PC Modal，mobile=H5 全屏，popup=Login_Popup */
  variant?: EmailLoginVariant;
  /** @deprecated 使用 variant="popup" */
  isPopup?: boolean;
  /** 验码成功回调：由全局 OSP_LOGIN_SUCCESS 处理登录态，此处仅用于关闭弹窗等 UI */
  onSuccess?: () => void;
  onClose?: () => void;
  /** 点击社交登录（Figma 流程一：首屏 or 下方三按钮） */
  onSocialClick?: (type: Web3LoginSupportedType) => void;
  /** 社交登录 loading 时禁用社交按钮 */
  socialLoading?: boolean;
  className?: string;
  /**
   * 点击登录相关按钮时上报埋点（与是否登录成功无关）
   * 等价于 login-modal.js 中的 detail.method：email / APPLE / GOOGLE / TWITTER 等
   */
  onLoginClick?: (method: string) => void;
  /** 验证码步骤埋点回调 */
  onTrack?: (
    event: EmailLoginTrackingEvent,
    detail?: Record<string, string>,
  ) => void;
  /** step 变化时通知外层（用于移动端布局切换） */
  onStepChange?: (step: EmailLoginStep) => void;
  /**
   * H5 Sign in 布局：flat=V3 Figma 扁平页（`/login` page）；legacy=旧 hero + sheet / modal
   */
  signInLayout?: "flat" | "legacy";
}

function resolveVariant(
  variant: EmailLoginVariant | undefined,
  isPopup: boolean,
): EmailLoginVariant {
  if (variant) {
    return variant;
  }
  return isPopup ? "popup" : "mobile";
}

export const EmailLoginFlow = forwardRef<
  EmailLoginFlowRef,
  EmailLoginFlowProps
>(function EmailLoginFlow(
  {
    variant,
    isPopup = false,
    onSuccess,
    onClose: _onClose,
    onSocialClick,
    socialLoading = false,
    className,
    onLoginClick,
    onTrack,
    onStepChange,
    signInLayout = "legacy",
  },
  ref,
) {
  const { device } = useParams<{ device: DeviceType }>();
  const resolvedVariant = resolveVariant(variant, isPopup);
  const useFlatSignInLayout =
    signInLayout === "flat" &&
    (resolvedVariant === "mobile" || resolvedVariant === "desktop");
  const harness = useOnboardingHarnessQuery();
  const harnessFixture =
    useFlatSignInLayout && harness.enabled
      ? {
          initialStep: harness.emailStep ?? undefined,
          codeError: harness.codeError,
          fixtureEmail:
            harness.emailStep === "code" ? "user@example.com" : undefined,
        }
      : undefined;
  const logic = useEmailLoginLogic({
    onSuccess,
    onSocialClick,
    socialLoading,
    onLoginClick,
    onTrack,
    onStepChange,
    harnessFixture,
  });
  const [isCodeInputFocused, setIsCodeInputFocused] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      getStep: () => logic.stepRef.current,
      goBackToEmail: logic.goBackToEmail,
    }),
    [logic.goBackToEmail, logic.stepRef],
  );

  const emailInputStep = (
    <EmailInputStep
      variant={resolvedVariant}
      {...logic.emailInputStepProps}
      termsLink={agreement.termsLink}
      privacyLink={agreement.privacyLink}
      hideTitle={useFlatSignInLayout}
      useV301SignInChrome={useFlatSignInLayout}
    />
  );

  const codeVerifyStep = (
    <CodeVerifyStep
      variant={resolvedVariant}
      {...logic.codeVerifyStepProps}
      useV301VerifyChrome={useFlatSignInLayout}
      onInputFocusChange={
        useFlatSignInLayout ? setIsCodeInputFocused : undefined
      }
    />
  );

  const renderEmailStep = () => {
    if (useFlatSignInLayout) {
      return <SignInPageLayout>{emailInputStep}</SignInPageLayout>;
    }

    if (resolvedVariant === "mobile") {
      return (
        <MobileLoginLayout step="email" onBackToEmail={logic.goBackToEmail}>
          {emailInputStep}
        </MobileLoginLayout>
      );
    }

    if (resolvedVariant === "desktop") {
      return <DesktopLoginLayout>{emailInputStep}</DesktopLoginLayout>;
    }

    return emailInputStep;
  };

  const isMobile = device === "android" || device === "ios";

  const renderCodeStep = () => {
    if (useFlatSignInLayout) {
      return (
        <VerifyEmailPageLayout collapseDecor={isCodeInputFocused && isMobile}>
          {codeVerifyStep}
        </VerifyEmailPageLayout>
      );
    }

    if (resolvedVariant === "mobile") {
      return (
        <MobileLoginLayout step="code" onBackToEmail={logic.goBackToEmail}>
          {codeVerifyStep}
        </MobileLoginLayout>
      );
    }

    return codeVerifyStep;
  };

  return (
    <div className={className}>
      {logic.step === "email" ? renderEmailStep() : renderCodeStep()}
    </div>
  );
});
